import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, Sparkles, Target, Search, ThumbsUp, ThumbsDown, Check, X, Network, TriangleAlert, Zap } from "lucide-react";
import { api, fmtUSD } from "../../services/api";
import AnimatedLineChart from "../charts/AnimatedLineChart";
import SentimentChart from "../charts/SentimentChart";
import DealerInventory from "../charts/DealerInventory";

function TypeMark({ shape, color }) {
  return <span className={`type-mark ${shape}`} style={{ "--mk": color }} />;
}

const SEV = {
  Critical: { color: "#dc2626", bg: "#fef2f2", ring: "#fecaca" },
  High: { color: "#ea580c", bg: "#fff7ed", ring: "#fed7aa" },
  Medium: { color: "#ca8a04", bg: "#fefce8", ring: "#fde68a" },
};

export default function DecisionCard({ s, defaultOpen }) {
  const [open, setOpen] = useState(!!defaultOpen);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fb, setFb] = useState({ useful: null, correct: null });
  const nav = useNavigate();
  const t = s.type_meta;
  const sev = SEV[s.severity] || SEV.Medium;

  useEffect(() => {
    if (open && !detail) {
      setLoading(true);
      api.vansDecision(s.id).then((d) => { setDetail(d); setLoading(false); });
      api.vansGetFeedback(s.id).then((f) => setFb({ useful: f.useful, correct: f.correct }));
    }
  }, [open]); // eslint-disable-line

  const sendFb = async (patch) => {
    const next = { ...fb, ...patch };
    setFb(next);
    await api.vansFeedback({ insight_id: s.id, ...next });
  };

  const d = s.deltas;
  const chips = [
    ["Search", d.search], ["Sales", d.sales], ["Conversion", d.conv], ["Vans Inv.", d.vansInv], ["Returns", d.returns],
  ].filter(([, v]) => Math.abs(v) >= 5);

  return (
    <article className={`decision-card ${open ? "open" : ""}`} style={{ "--accent": t.color }}>
      <div className="dc-accent" />
      <button className="dc-head" onClick={() => setOpen((v) => !v)}>
        <span className="dc-rank">#{s.rank}</span>
        <span className="type-pill lg" style={{ background: t.soft, color: t.text }}>
          <TypeMark shape={t.shape} color={t.color} /> {s.type_label}
        </span>
        <div className="dc-head-main">
          <div className="dc-title">{s.product} <span className="dot-sep">·</span> <span className="dc-region">{s.region}</span></div>
          <div className="dc-headline">{s.headline}</div>
        </div>
        <div className="dc-right">
          <span className="sev-badge" style={{ background: sev.bg, color: sev.color, borderColor: sev.ring }}>{s.severity}</span>
          {s.detection && (
            <span className="earlier-badge" title={`DotIQ correlated this on ${s.detection.dotiq_week}; a ${s.detection.single_metric}-only view wouldn't have flagged it until ${s.detection.single_week}.`}>
              <Zap size={12} /> {s.detection.weeks_earlier} wk{s.detection.weeks_earlier > 1 ? "s" : ""} earlier
            </span>
          )}
          <div className="dc-conf"><b>{s.confidence}%</b> confidence</div>
          <div className="dc-impact">{fmtUSD(s.impact)}</div>
        </div>
        <ChevronDown size={18} className={`chev ${open ? "open" : ""}`} />
      </button>

      {open && (
        <div className="dc-body">
          {loading || !detail ? (
            <div className="insight-loading">Generating explanation…</div>
          ) : (
            <>
              <div className="delta-chips">
                {chips.map(([label, v]) => (
                  <span key={label} className={`delta-chip ${v >= 0 ? "up" : "down"}`}>
                    {label} {v >= 0 ? "+" : ""}{v}%
                  </span>
                ))}
                <span className="delta-chip sys">{s.systems.length} systems correlated</span>
              </div>

              {s.detection && (
                <div className="earlier-strip">
                  <Zap size={15} />
                  <span>
                    DotIQ correlated this on <b>{s.detection.dotiq_week}</b>. A team watching only{" "}
                    <b>{s.detection.single_metric}</b> wouldn't have flagged it until <b>{s.detection.single_week}</b> —{" "}
                    DotIQ caught it <b>~{s.detection.weeks_earlier} week{s.detection.weeks_earlier > 1 ? "s" : ""} earlier</b>.
                  </span>
                </div>
              )}

              <div className="ai-block">
                <div className="ai-row">
                  <div className="ai-icon"><Sparkles size={15} /></div>
                  <div><div className="ai-label">Why it matters</div><p>{detail.explanation?.why_it_matters}</p></div>
                </div>
                <div className="ai-row">
                  <div className="ai-icon rec"><Target size={15} /></div>
                  <div><div className="ai-label">Recommended action</div><p>{detail.explanation?.recommended_action}</p></div>
                </div>
                <div className="ai-row">
                  <div className="ai-icon"><Search size={15} /></div>
                  <div><div className="ai-label">Why this confidence</div><p>{detail.explanation?.confidence_rationale}</p></div>
                </div>
              </div>

              {s.promo_flag && (
                <div className="promo-banner"><TriangleAlert size={15} /> Promo-driven lift detected ({d.promo_share}% of weeks on promotion) — treat as brand heat, not organic demand.</div>
              )}

              <div className="reasoning-path">
                <div className="rp-label">Reasoning path</div>
                <div className="rp-chain">
                  {detail.path_nodes.map((n, i) => (
                    <React.Fragment key={n.id}>
                      <span className={`rp-node t-${n.type}`}>{n.label}</span>
                      {i < detail.path_nodes.length - 1 && <span className="rp-arrow">→</span>}
                    </React.Fragment>
                  ))}
                </div>
              </div>

              <div className="chart-card">
                <AnimatedLineChart
                  series={detail.chart_series.map((cs) => ({ key: cs.key, label: cs.label, color: cs.color, values: cs.values }))}
                  labels={detail.weeks}
                />
              </div>

              {detail.dealers && <DealerInventory dealers={detail.dealers} date={detail.weeks[detail.weeks.length - 1]} />}

              {detail.sentiment && (
                <div className="chart-card">
                  <div className="sub-chart-title">Review sentiment (-1 to +1)</div>
                  <SentimentChart
                    data={detail.weekly.map((w) => w.sentiment)}
                    labels={detail.weeks}
                    turnedNegativeWeek={detail.sentiment.turnedNegativeWeek}
                  />
                </div>
              )}

              <div className="evidence-grid">
                <div className="ev-side">
                  <div className="ev-h">Supporting evidence</div>
                  <ul className="ev-list plain">
                    {detail.evidence.map((e, i) => (
                      <li key={i}><span className="ev-bullet" style={{ background: t.color }} />{e}</li>
                    ))}
                  </ul>
                </div>
                <div className="ev-side">
                  <div className="ev-h">Rule check (all 3 rules)</div>
                  <table className="rule-table">
                    <thead><tr><th>Rule</th><th>Result</th><th>Conf.</th></tr></thead>
                    <tbody>
                      {detail.rules.map((r) => (
                        <tr key={r.name} className={r.result === "fired" ? "fired" : ""}>
                          <td>{r.name}</td>
                          <td>{r.result === "fired" ? <span className="fired-tag">fired</span> : "—"}</td>
                          <td>{r.conf != null ? r.conf + "%" : "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="insight-actions">
                <button className="btn-ghost sm" onClick={() => nav(`/vans/graph?focus=prod:${s.product.toLowerCase().replace(/vans /, "").replace(/ /g, "-")}`)}>
                  <Network size={15} /> View on context graph
                </button>
                <div className="fb-group">
                  <span className="fb-label">Useful?</span>
                  <button className={`fb-btn ${fb.useful === true ? "on-good" : ""}`} onClick={() => sendFb({ useful: true })}><ThumbsUp size={14} /></button>
                  <button className={`fb-btn ${fb.useful === false ? "on-bad" : ""}`} onClick={() => sendFb({ useful: false })}><ThumbsDown size={14} /></button>
                  <span className="fb-label">Correct?</span>
                  <button className={`fb-btn ${fb.correct === true ? "on-good" : ""}`} onClick={() => sendFb({ correct: true })}><Check size={14} /></button>
                  <button className={`fb-btn ${fb.correct === false ? "on-bad" : ""}`} onClick={() => sendFb({ correct: false })}><X size={14} /></button>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </article>
  );
}
